function serviceWorkerPath(): string {
 const raw = process.env.PUBLIC_URL ?? ''
 const path = !raw || raw === '/'? '/sw.js': raw === '.'|| raw === './'? './sw.js': `${raw.replace(/\/$/, '')}/sw.js`
 const version = currentBundleVersion()
 return `${path}${path.includes('?') ? '&' : '?'}v=${encodeURIComponent(version)}`
}

function currentBundleVersion(): string {
 if (typeof document === 'undefined') return 'v4'

 const scripts = Array.from(document.scripts)
 .map((script) => script.src)
 .filter(Boolean)

 const mainBundle = scripts.find((src) => /\/static\/js\/main\.[^/?]+\.js(?:[?#].*)?$/i.test(src))
 const staticBundle = mainBundle ?? scripts.find((src) => /\/static\/js\/[^/?]+\.js(?:[?#].*)?$/i.test(src))
 const hash = staticBundle?.match(/\/static\/js\/(?:main\.)?([a-f0-9]{8})[^/]*\.js/i)?.[1]

 return hash ?? 'v4'
}

function isElectronRuntime(): boolean {
 if (typeof window === 'undefined') return false
 return window.location.protocol === 'file:' || Boolean((window as any)?.electronAPI?.isElectron)
}

const DESKTOP_SW_CLEANUP_RELOAD_KEY = 'desktop-sw-cleanup-reloaded'
const SW_CONTROLLER_RELOAD_KEY = 'erp-sw-controller-reloaded'

function markControllerReload(): boolean {
 try {
 if (sessionStorage.getItem(SW_CONTROLLER_RELOAD_KEY) === '1') return false
 sessionStorage.setItem(SW_CONTROLLER_RELOAD_KEY, '1')
 return true
 } catch {
 return true
 }
}

function clearControllerReloadMark(): void {
 try {
 sessionStorage.removeItem(SW_CONTROLLER_RELOAD_KEY)
 } catch {
 /* ignore storage errors */
 }
}

function hasReloadedAfterDesktopCleanup(): boolean {
 try {
 return sessionStorage.getItem(DESKTOP_SW_CLEANUP_RELOAD_KEY) === '1'
 } catch {
 return false
 }
}

function markReloadedAfterDesktopCleanup(): void {
 try {
 sessionStorage.setItem(DESKTOP_SW_CLEANUP_RELOAD_KEY, '1')
 } catch {
 /* ignore storage errors */
 }
}

function clearReloadedAfterDesktopCleanup(): void {
 try {
 sessionStorage.removeItem(DESKTOP_SW_CLEANUP_RELOAD_KEY)
 } catch {
 /* ignore storage errors */
 }
}

async function removeDesktopServiceWorker(): Promise<void> {
 const hasController = Boolean(navigator.serviceWorker.controller)

 try {
 const registrations = await navigator.serviceWorker.getRegistrations()
 await Promise.all(registrations.map((registration) => registration.unregister()))
 } catch {
 /* ignore cleanup errors */
 }

 try {
 if ('caches' in window) {
 const cacheNames = await caches.keys()
 await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)))
 }
 } catch {
 /* ignore cleanup errors */
 }

 if (hasController && !hasReloadedAfterDesktopCleanup()) {
 markReloadedAfterDesktopCleanup()
 window.location.reload()
 return
 }

 if (!hasController) {
 clearReloadedAfterDesktopCleanup()
 }
}

export function registerPosServiceWorker(): void {
 if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) {
 return
 }

 if (isElectronRuntime()) {
 void removeDesktopServiceWorker()
 return
 }

 window.addEventListener('load', () => {
 const hadController = Boolean(navigator.serviceWorker.controller)
 let refreshing = false

 navigator.serviceWorker.addEventListener('controllerchange', () => {
 if (!hadController || refreshing) return
 refreshing = true
 if (markControllerReload()) {
 window.location.reload()
 }
 })

 navigator.serviceWorker
 .register(serviceWorkerPath(), { updateViaCache: 'none' })
 .then((registration) => {
 clearControllerReloadMark()
 requestWaitingWorkerActivation(registration)

 registration.addEventListener('updatefound', () => {
 const worker = registration.installing
 if (!worker) return

 worker.addEventListener('statechange', () => {
 if (worker.state === 'installed' && navigator.serviceWorker.controller) {
 requestWaitingWorkerActivation(registration)
 }
 })
 })

 const update = () => registration.update().catch(() => undefined)
 update()
 window.setInterval(update, 5 * 60 * 1000)
 document.addEventListener('visibilitychange', () => {
 if (document.visibilityState === 'visible') update()
 })
 })
 .catch(() => {
 /* ignore registration errors */
 })
 })
}

function requestWaitingWorkerActivation(registration: ServiceWorkerRegistration): void {
 const waiting = registration.waiting
 if (!waiting) return
 waiting.postMessage({ type: 'SKIP_WAITING' })
}
